/**
 * Authentication Context for VizzEx External App
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { User, Organization, Site } from '../lib/api';
import { authApi, isAuthenticated, removeToken } from '../lib/api';

// ============================================
// TYPES
// ============================================

interface AuthState {
  user: User | null;
  organizations: Organization[];
  currentOrganizationId: number | null;
  currentSite: Site | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; otpRequired?: boolean }>;
  verifyLoginOtp: (email: string, otp: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, name?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setCurrentOrganization: (id: number) => void;
  setCurrentSite: (site: Site | null) => void;
}

// ============================================
// CONTEXT
// ============================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    organizations: [],
    currentOrganizationId: null,
    currentSite: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Debug logging helper
  const debugLog = (step: string, data?: unknown) => {
    const logs = JSON.parse(localStorage.getItem('vizzex_debug_log') || '[]');
    logs.push({
      time: new Date().toISOString(),
      step: `[AuthContext] ${step}`,
      data: data ? JSON.stringify(data) : undefined
    });
    if (logs.length > 50) logs.shift();
    localStorage.setItem('vizzex_debug_log', JSON.stringify(logs));
  };

  /**
   * Fetch current user data
   */
  const refreshUser = useCallback(async () => {
    debugLog('refreshUser called', { hasToken: isAuthenticated() });
    
    if (!isAuthenticated()) {
      debugLog('No token found, setting isAuthenticated=false');
      setState(prev => ({ ...prev, isLoading: false, isAuthenticated: false }));
      return;
    }

    try {
      debugLog('Calling getCurrentUser API');
      const data = await authApi.getCurrentUser();
      debugLog('getCurrentUser response', { success: data.success, hasUser: !!data.user });
      
      if (data.success && data.user) {
        debugLog('Setting authenticated state with user');
        setState({
          user: data.user,
          organizations: data.organizations || [],
          currentOrganizationId: data.currentOrganizationId || null,
          currentSite: data.currentSite || null,
          isLoading: false,
          isAuthenticated: true,
        });
      } else {
        // Token invalid
        debugLog('Token invalid, removing token', { response: data });
        removeToken();
        setState({
          user: null,
          organizations: [],
          currentOrganizationId: null,
          currentSite: null,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    } catch (error) {
      debugLog('getCurrentUser error', { error: String(error), message: (error as Error)?.message });
      console.error('Failed to fetch user:', error);
      removeToken();
      setState({
        user: null,
        organizations: [],
        currentOrganizationId: null,
        currentSite: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  }, []);

  // Load user on mount
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  /**
   * Login with email/password
   */
  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string; otpRequired?: boolean }> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      const response = await authApi.login(email, password);

      // Server returns otpRequired:true when the account needs post-lockout verification
      if (response.otpRequired) {
        setState(prev => ({ ...prev, isLoading: false }));
        return { success: false, otpRequired: true };
      }
      
      if (response.success && response.user) {
        // Use refreshUser so getCurrentUser runs and returns the session's currentSite.
        // This ensures any previously connected site is restored on every login.
        await refreshUser();
        return { success: true };
      }
      
      setState(prev => ({ ...prev, isLoading: false }));
      return { success: false, error: 'Login failed' };
    } catch (error: unknown) {
      setState(prev => ({ ...prev, isLoading: false }));
      const axiosError = error as { response?: { data?: { error?: string; code?: string } } };
      const code = axiosError.response?.data?.code;
      if (code === 'ACCOUNT_LOCKED') {
        return { success: false, error: axiosError.response?.data?.error || 'Account temporarily locked.' };
      }
      return { 
        success: false, 
        error: axiosError.response?.data?.error || 'Login failed. Please try again.' 
      };
    }
  };

  /**
   * Verify login OTP after account lockout
   */
  const verifyLoginOtp = async (email: string, otp: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      const response = await authApi.verifyLoginOtp(email, otp);
      if (response.success && response.user) {
        await refreshUser();
        return { success: true };
      }
      setState(prev => ({ ...prev, isLoading: false }));
      return { success: false, error: 'Verification failed' };
    } catch (error: unknown) {
      setState(prev => ({ ...prev, isLoading: false }));
      const axiosError = error as { response?: { data?: { error?: string } } };
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Invalid or expired code. Please try again.'
      };
    }
  };

  /**
   * Register new user
   */
  const register = async (email: string, password: string, name?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      const response = await authApi.register(email, password, name);
      
      if (response.success && response.user) {
        setState({
          user: response.user,
          organizations: response.organization ? [response.organization] : [],
          currentOrganizationId: response.organization?.id || null,
          currentSite: null,
          isLoading: false,
          isAuthenticated: true,
        });
        return { success: true };
      }
      
      setState(prev => ({ ...prev, isLoading: false }));
      return { success: false, error: 'Registration failed' };
    } catch (error: unknown) {
      setState(prev => ({ ...prev, isLoading: false }));
      const axiosError = error as { response?: { data?: { error?: string } } };
      return { 
        success: false, 
        error: axiosError.response?.data?.error || 'Registration failed. Please try again.' 
      };
    }
  };

  /**
   * Logout
   */
  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setState({
        user: null,
        organizations: [],
        currentOrganizationId: null,
        currentSite: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  };

  /**
   * Set current organization
   */
  const setCurrentOrganization = (id: number) => {
    setState(prev => ({ ...prev, currentOrganizationId: id }));
  };

  /**
   * Set current site
   */
  const setCurrentSite = (site: Site | null) => {
    setState(prev => ({ ...prev, currentSite: site }));
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        verifyLoginOtp,
        register,
        logout,
        refreshUser,
        setCurrentOrganization,
        setCurrentSite,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;

